import { type ReactNode, useEffect, useRef } from "react";
import { useLocation } from "react-router";
import { Button } from "../components/ui/Button";
import { cx } from "../components/ui/cx";
import { Icon, type IconName } from "../components/ui/Icon";
import { TRACK_NAMES, type TrackKind, trackProblem } from "./rules";
import { BackButton, Sq, type StepProps } from "./SetupView";
import { type Card, useTrackWork } from "./useTracking";

const LOGOS: Record<TrackKind, ReactNode> = {
  meta: (
    <div className="lgx" style={{ background: "#e8f0fe", color: "#1d4fb8" }}>
      M
    </div>
  ),
  ga: (
    <div className="lgx" style={{ background: "#fdf0dc", color: "#9a5a00" }}>
      G
    </div>
  ),
  own: (
    <div className="lgx" style={{ background: "var(--surface-3)", color: "var(--ink-2)" }}>
      <Icon name="code" />
    </div>
  ),
};
export { LOGOS as TRACK_LOGOS };

// --- 4. Tracking ---

export function TrackingStep({ data, run, setData }: StepProps) {
  const opened = (useLocation().state as { open?: TrackKind } | null)?.open;
  const { cards, patch, busy, checkLoads, finish, skip, tokenSaved } = useTrackWork(data, run, setData, opened);
  return (
    <div className="sbody">
      <Sq
        eyebrow="Step 4 of 4 · Tracking · Optional"
        title="Do you run ads on Meta or Google?"
        foot={
          <>
            <BackButton to="/setup/address" />
            <span className="sp" />
            <Button variant="q" size="lg" disabled={busy} onClick={() => void skip()}>
              Skip this
            </Button>
            <Button variant="p" size="lg" after="right" disabled={busy} onClick={() => void finish()}>
              Continue
            </Button>
          </>
        }
      >
        {(["meta", "ga", "own"] as const).map((k) => (
          <TrackCard
            key={k}
            kind={k}
            card={cards[k]}
            tokenSaved={tokenSaved}
            onToggle={() => patch(k, { on: !cards[k].on, st: "none" })}
            onChange={(p) => patch(k, { ...p, st: "none" })}
            onCheck={() => void checkLoads(k)}
          />
        ))}
      </Sq>
      <div className="sstage">
        <div className="pvbar">
          <div className="stage-cap">
            <Icon name="bars" />{" "}
            <span>
              <b>What your tools hear</b>
            </span>
          </div>
        </div>
        <EventList meta={cards.meta.on} ga={cards.ga.on} />
      </div>
    </div>
  );
}

function TrackCard({
  kind,
  card,
  tokenSaved,
  onToggle,
  onChange,
  onCheck,
}: {
  kind: TrackKind;
  card: Card;
  tokenSaved: boolean;
  onToggle: () => void;
  onChange: (p: Partial<Card>) => void;
  onCheck: () => void;
}) {
  const id = `trk-${kind}`;
  const box = useRef<HTMLDivElement>(null);
  const was = useRef(card.on);
  // Why: a card opened near the bottom grows under the step's buttons; it comes up into view as it opens.
  useEffect(() => {
    if (card.on && !was.current) box.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
    was.current = card.on;
  }, [card.on]);
  const status =
    card.st === "loaded" ? (
      <span className="rst found">
        <span className="ck">
          <Icon name="check" />
        </span>
        Loaded on your pages
      </span>
    ) : card.st === "checking" ? (
      <span className="rst checking">
        <span className="spin" />
        Opening your sign-up page
      </span>
    ) : card.st === "waiting" ? (
      <span className="rst">
        <span className="o" />
        Waiting for a visit
      </span>
    ) : card.st === "bad" ? (
      <span className="small" style={{ color: "var(--bad)" }}>
        {trackProblem(kind, card.value)}
      </span>
    ) : null;
  return (
    <div className={cx("tcard", card.on && "on")} ref={box}>
      <button type="button" className="th2" style={{ width: "100%", background: "none", border: 0, textAlign: "left" }} aria-expanded={card.on} onClick={onToggle}>
        {LOGOS[kind]}
        <div className="sp">
          <div className="b7">{TRACK_NAMES[kind]}</div>
        </div>
        <span className={cx("check", card.on && "on")} aria-hidden="true">
          {card.on ? <Icon name="check" /> : null}
        </span>
      </button>
      {card.on ? (
        <div className="tb">
          {kind === "meta" ? (
            <>
              <div className="field">
                <label htmlFor={id}>Pixel ID</label>
                <input dir="auto" id={id} className="inp" placeholder="15 or 16 digits" inputMode="numeric" value={card.value} onChange={(e) => onChange({ value: e.target.value })} />
                <div className="hint">In Meta Events Manager, under Data sources.</div>
              </div>
              <div className="field">
                <label htmlFor="trk-token">
                  Conversions API token{" "}
                  <span className="muted" style={{ fontWeight: 500 }}>
                    (optional)
                  </span>
                </label>
                <input
                  id="trk-token"
                  className="inp"
                  type="password"
                  autoComplete="off"
                  placeholder={tokenSaved ? "Saved. Paste a new one to replace it" : "Paste the token"}
                  value={card.token}
                  onChange={(e) => onChange({ token: e.target.value })}
                />
              </div>
            </>
          ) : kind === "ga" ? (
            <div className="field">
              <label htmlFor={id}>Measurement ID</label>
              <input dir="auto" id={id} className="inp" placeholder="G-XXXXXXXXXX" spellCheck={false} value={card.value} onChange={(e) => onChange({ value: e.target.value })} />
              <div className="hint">In Google Analytics, under Admin, then Data streams.</div>
            </div>
          ) : (
            <div className="field">
              <label htmlFor={id}>Paste the code your tool gave you</label>
              <textarea dir="auto" id={id} className="inp mono" rows={3} placeholder="<script>…</script>" spellCheck={false} value={card.value} onChange={(e) => onChange({ value: e.target.value })} />
            </div>
          )}
          <div className="row g12 wrap">
            <Button size="s" icon="refresh" disabled={card.st === "checking"} onClick={onCheck}>
              Check it loads
            </Button>
            <span aria-live="polite">{status}</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export const EVENTS: Array<[IconName, string, string, string | null]> = [
  ["eye", "Opened a sign-up page", "Page view", "page_view"],
  ["userplus", "Registered", "Lead", "generate_lead"],
  ["door", "Came to the room", "Came", "video_start"],
  ["flag", "Watched to the end", "Watched", "video_complete"],
  // Why no Google name: the offer press goes to Meta only.
  ["pointer", "Clicked the offer", "Clicked offer", null],
];

/** What each tool is told, moment by moment, with the names it hears them by. */
function EventList({ meta, ga }: { meta: boolean; ga: boolean }) {
  return (
    <div className="evlist">
      {EVENTS.map(([icon, title, metaName, gaName]) => (
        <div key={title} className="evli">
          <div className="ic">
            <Icon name={icon} />
          </div>
          <div className="b7">{title}</div>
          <div className="row g4">
            {meta ? <span className="tool-on meta">{metaName}</span> : null}
            {ga && gaName ? <span className="tool-on ga">{gaName}</span> : null}
            {!meta && !(ga && gaName) ? <span className="tool-on off">Nothing yet</span> : null}
          </div>
        </div>
      ))}
    </div>
  );
}
